import { z } from "zod";

import { ctaSchema } from "./cta";
import { formSchema } from "./forms";
import { historyInformationSchema } from "./history-information";
import { iconSchema } from "./icon";
import { imageSchema } from "./image";
import { memberSchema } from "./member";
import { richTextSchema } from "./rich-text";

export const plainTextBlockSchema = z.object({
  type: z.literal("plainTextBlock"),
  content: richTextSchema,
  className: z.string().optional(),
});

const comparisonTableImageSchema = imageSchema;

export const comparisonTableSchema = z.object({
  type: z.literal("comparisonTable"),
  title: z.string().nullable(),
  description: richTextSchema.nullable(),
  table: z.object({
    header: z.array(
      z.discriminatedUnion("type", [
        z.object({
          type: z.literal("headerCellText"),
          text: z.string(),
        }),
        z.object({
          type: z.literal("headerCellImage"),
          default: comparisonTableImageSchema.nullable(),
          mobile: comparisonTableImageSchema.nullable(),
        }),
      ])
    ),
    rows: z.array(
      z.object({
        type: z.literal("row"),
        rowCells: z.array(
          z.discriminatedUnion("type", [
            z.object({
              type: z.literal("cellText"),
              text: z.string(),
            }),
            z.object({
              type: z.literal("checkCell"),
              tone: z.enum(["blue", "gray"]),
              text: z.string().optional(),
            }),
            iconSchema,
          ])
        ),
      })
    ),
    highlightedColumnNumber: z.number().min(1),
  }),
  cta: z
    .object({
      label: z.string(),
      url: z.string(),
      variant: z.enum(["default", "secondary"]),
      external: z.boolean(),
    })
    .nullable(),
  className: z.string().optional(),
});

export const comparisonTableV2Schema = z.object({
  type: z.literal("comparisonTableV2"),
  title: z.string().nullable(),
  columns: z.array(
    z.object({
      title: z.string(),
      rows: z.array(
        z.object({
          text: richTextSchema.nullable(),
          icon: z.enum(["check", "minus"]).nullable(),
        })
      ),
    })
  ),
});

export const formBlockSchema = z.object({
  type: z.literal("formBlock"),
  title: z.string(),
  description: richTextSchema.nullable(),
  form: formSchema,
  background: z.enum(["light", "gray"]),
  identifier: z.string().nullable(),
  className: z.string().optional(),
});

export const postsBlockSchema = z.object({
  type: z.literal("postsBlock"),
  title: z.string().nullable(),
  description: richTextSchema.nullable(),
  category: z.string().nullable(),
  className: z.string().optional(),
});

export const articleSchema = z.object({
  type: z.literal("article"),
  content: richTextSchema,
  className: z.string().optional(),
});

const videoSchema = z.object({
  asset: z.object({
    playbackId: z.string(),
    resolution: z.string(),
    aspectRatio: z.string(),
  }),
});

export const heroSchema = z.object({
  type: z.literal("hero"),
  title: richTextSchema.nullable(),
  headingTag: z.enum(["h1", "h2", "h3"]).nullable(),
  subtitle: richTextSchema.nullable(),
  subtitleLineHeight: z.enum(["normal", "tight", "relaxed"]).nullable(),
  subtitleFontWeight: z.enum(["normal", "semibold", "bold"]).nullable(),
  theme: z.enum(["dark", "light"]).nullable(),
  label: z.string().nullable(),
  cta: ctaSchema.nullable(),
  form: formSchema.nullable(),
  ctaGridEnabled: z.boolean().nullable(),
  withImageBorder: z.boolean().nullable(),
  imageBlurEnabled: z.boolean().nullable(),
  trustpilotLabel: z.string().nullable(),
  complianceLabel: z.string().nullable(),
  supportingText: richTextSchema.nullable(),
  image: imageSchema.nullable(),
  mobileImage: imageSchema.extend({ alt: z.string().nullish() }).nullable(),
  videoAnimation: videoSchema.nullable().optional(),
  imageScrollAnimation: z
    .object({
      initialOffset: z.number().nullable(),
      scrollDistance: z.number().nullable(),
    })
    .nullable()
    .optional(),
  className: z.string().optional(),
});

export const introBlockSchema = z.object({
  type: z.literal("introBlock"),
  title: z.string(),
  headingTag: z.enum(["h1", "h2", "h3"]).nullable(),
  background: z.enum(["blue", "light", "black"]).nullable(),
  description: richTextSchema.nullable(),
  trustpilot: z.boolean().nullable(),
  trustpilotLabel: z.string().nullable(),
  cta: ctaSchema.nullable(),
  secondaryCta: ctaSchema.nullable(),
  className: z.string().optional(),
});

export const calloutBannerSchema = z.object({
  type: z.literal("calloutBanner"),
  caption: z.string(),
  title: z.string(),
  subtitle: richTextSchema.nullable(),
  images: z.array(imageSchema).max(3).nullable(),
  cta: ctaSchema.nullable(),
  secondaryCta: ctaSchema.nullable(),
  form: formSchema.nullable(),
  className: z.string().optional(),
});

export const largeCardSectionSchema = z.object({
  type: z.literal("largeCardSection"),
  title: richTextSchema.nullable(),
  description: richTextSchema.nullable(),
  mediaType: z.enum(["image", "video", "videoAnimation", "animation"]),
  bgColor: z
    .object({
      value: z.string().nullable(),
    })
    .nullable(),
  image: imageSchema.extend({ alt: z.string() }).nullable(),
  video: videoSchema.nullable(),
  mobileVideo: videoSchema.nullable(),
  videoAnimation: videoSchema.nullable(),
  mobileVideoAnimation: videoSchema.nullable(),
  animation: z.string().nullable(),
  mobileAnimation: z.string().nullable(),
  loopAnimation: z.boolean().nullable(),
  isLightText: z.boolean().nullable(),
});

export const largeCardSchema = z.object({
  type: z.literal("largeCard"),
  section: largeCardSectionSchema.nullable(),
  background: z.enum(["blue", "light", "black"]).nullable(),
  className: z.string().optional(),
  desktopGridColumn: z.enum(["10", "12"]).nullable(),
});

export const customCardSchema = z.object({
  variant: z.enum([
    "inset",
    "withLink",
    "linkNoMedia",
    "withTimer",
    "vertical",
    "withLeftBorder",
  ]),
  alignment: z.enum(["left", "center"]).nullable(),
  isLightText: z.boolean().nullable(),
  title: z.string().nullable(),
  richTextTitle: richTextSchema.nullable(),
  icon: z.string().nullable(),
  description: z.string().nullable(),
  label: z.string().nullable(),
  time: z.string().nullable(),
  mediaType: z
    .enum(["image", "video", "animation", "videoAnimation"])
    .nullable(),
  bgColor: z
    .object({
      value: z.string().nullable(),
    })
    .nullable(),
  image: imageSchema.extend({ alt: z.string() }).nullable(),
  video: videoSchema.nullable(),
  videoAnimation: videoSchema.nullable(),
  animation: z.string().nullable(),
  loopAnimation: z.boolean().nullable(),
  url: z.string().nullable(),
  external: z.boolean().nullable(),
});

export const threeCardsSectionSchema = z.object({
  type: z.literal("threeCardsSection"),
  cards: z.array(customCardSchema),
});

export const featuredCardsSchema = z.object({
  type: z.literal("featuredCards"),
  title: z.string(),
  largeCard: largeCardSectionSchema.nullable(),
  threeCards: threeCardsSectionSchema.nullable(),
  className: z.string().optional(),
});

export const articleListSchema = z.object({
  type: z.literal("articleList"),
  title: z.string(),
  cta: ctaSchema,
  articleLayout: z.enum(["one-line", "two-lines"]),
  articleType: z.enum(["news", "press"]),
  press: z
    .array(
      z.object({
        type: z.literal("pressRelease"),
        title: z.string(),
        thumbnailImage: imageSchema,
        publishDate: z.string(),
        slug: z.string(),
      })
    )
    .nullable(),
  news: z
    .array(
      z.object({
        type: z.literal("newsArticle"),
        title: z.string(),
        publishDate: z.string(),
        url: z.string(),
        thumbnailImage: imageSchema,
        publication: z.string(),
      })
    )
    .nullable(),
  className: z.string().optional(),
});

export const tierBanksSchema = z.object({
  type: z.literal("tierBanks"),
  withTopBorder: z.boolean().nullable(),
  title: z.string(),
  description: richTextSchema,
  images: z.array(imageSchema),
  className: z.string().optional(),
});

export const leadershipTeamSchema = z.object({
  type: z.literal("leadershipTeam"),
  title: z.string(),
  description: richTextSchema,
  teamMembers: z.array(memberSchema),
  className: z.string().optional(),
});

export const customTrustPilotSchema = z.object({
  type: z.literal("customTrustPilot"),
  title: richTextSchema,
  className: z.string().optional(),
  background: z.enum(["purple", "dark-teal", "blue"]).nullable(),
  hideTrustpilotText: z.boolean().nullable(),
});

const sliderSchema = z.object({
  title: z.string().optional(),
  monthlyRevenue: z.string().optional(),
  estimatedEligibility: z.string().optional(),
  footerNote: z.string().optional(),
  minValue: z.number().min(9999).max(1000000),
  maxValue: z.number().min(9999).max(5000001),
  initialValue: z.number().min(9999).max(5000001),
  lowerMultiplier: z.number().min(0).max(5).optional(),
  upperMultiplier: z.number().min(0).max(5).optional(),
});

export const eligibilitySliderSchema = z.object({
  type: z.literal("eligibilitySliderSection"),
  title: z.string(),
  description: z.string().nullable(),
  cta: ctaSchema.nullable(),
  slider: sliderSchema,
  withTopBorder: z.boolean().nullable(),
  className: z.string().optional(),
});

const videoCardSchema = z.object({
  type: z.literal("videoCard"),
  title: z.string(),
  description: z.string(),
  video: videoSchema,
});

export const videoCarouselSchema = z.object({
  type: z.literal("videoCarousel"),
  variant: z.enum(["light", "dark"]),
  title: z.string(),
  cta: ctaSchema,
  videos: z.array(videoCardSchema),
  className: z.string().optional(),
});

export const cardsCarouselSchema = z.object({
  type: z.literal("cardsCarousel"),
  cards: z
    .array(
      z.object({
        title: z.string().nullable(),
        description: z.string().nullable(),
        image: imageSchema,
      })
    )
    .nullable(),
  className: z.string().optional(),
});

const featureCardSchema = z.object({
  type: z.literal("featureCard"),
  id: z.string().optional(),
  title: z.string().nullable(),
  titleSuperscript: z.string().nullable(),
  description: z.string().nullable(),
  isCenteredMedia: z.boolean().nullable(),
  mediaType: z.enum(["image", "video", "videoAnimation", "animation"]),
  bgColor: z
    .object({
      value: z.string().nullable(),
    })
    .nullable(),
  image: imageSchema.nullable(),
  video: videoSchema.nullable(),
  videoAnimation: videoSchema.nullable(),
  mobileVideoAnimation: videoSchema.nullable(),
  animation: z.string().nullable(),
  mobileAnimation: z.string().nullable(),
  loopAnimation: z.boolean().nullable(),
  isLightText: z.boolean().nullable(),
});

export const featuresGridSchema = z.object({
  type: z.literal("featuresGrid"),
  features: z.array(featureCardSchema).nullable(),
  layoutType: z.enum(["grid", "row"]),
  sequentiallyAnimate: z.boolean().nullable(),
  className: z.string().optional(),
});

export const heroCtaBannerSectionSchema = z.object({
  _id: z.string(),
  type: z.literal("heroCtaBannerSection"),
  title: z.string(),
  subtitle: richTextSchema,
  background: z.string(),
  cta: ctaSchema,
  firstImage: imageSchema.nullable(),
  secondImage: imageSchema.nullable(),
});

export const mediaWithAccordionsSchema = z.object({
  type: z.literal("mediaWithAccordions"),
  title: z.string(),
  description: richTextSchema.nullable(),
  image: imageSchema.nullable(),
  accordions: z.array(
    z.object({
      title: z.string(),
      description: richTextSchema.nullable(),
    })
  ),
  className: z.string().optional(),
});

export const heroCtaBannerSchema = z.object({
  type: z.literal("heroCtaBanner"),
  section: heroCtaBannerSectionSchema.nullable(),
  className: z.string().optional(),
});

export const companyHistorySchema = z.object({
  type: z.literal("companyHistory"),
  title: z.string(),
  rows: z.array(historyInformationSchema),
  className: z.string().optional(),
});

const customImageSchema = imageSchema.extend({ alt: z.string() });

export const mediaTextCardSchema = z.object({
  type: z.literal("mediaTextCard"),
  layoutType: z.enum(["full-width-text", "indented-text"]).nullable(),
  textSize: z.enum(["small", "medium", "large"]).nullable(),
  title: richTextSchema,
  description: richTextSchema,
  isHero: z.boolean().nullable(),
  cta: ctaSchema.nullable(),
  mediaType: z.enum(["image", "video", "animation", "videoAnimation"]),
  image: customImageSchema.nullable(),
  video: videoSchema.nullable(),
  videoAnimation: videoSchema.nullable(),
  animation: z.string().nullable(),
  loopAnimation: z.boolean().nullable(),
  bgColor: z
    .object({
      value: z.string().nullable(),
    })
    .nullable(),
  hideMediaOnMobile: z.boolean().nullable(),
});

export const mediaTextBlockSchema = z.object({
  type: z.literal("mediaTextBlock"),
  cards: z.array(mediaTextCardSchema),
  className: z.string().optional(),
  background: z.enum(["blue", "light", "blueGradient"]).nullable(),
  invertColumns: z.boolean().nullable(),
  staggeredAnimation: z.boolean().nullable(),
});

const testimonialSchema = z.object({
  type: z.literal("testimonial"),
  quote: richTextSchema,
  avatar: imageSchema.extend({ alt: z.string() }),
  name: z.string(),
  title: z.string(),
  description: z.string().nullable(),
});

export const testimonialsSchema = z.object({
  type: z.literal("testimonials"),
  title: z.string(),
  testimonialVariant: z.enum(["card", "slider"]),
  testimonials: z.array(testimonialSchema),
  className: z.string().optional(),
});

const flipCardSchema = z.object({
  type: z.literal("flipCard"),
  title: z.string(),
  subtitle: z.string().nullable(),
  icon: z.string(),
  label: z.string(),
  description: z.string(),
});

export const flipCardsSectionSchema = z.object({
  type: z.literal("flipCardsSection"),
  theme: z.enum(["blue", "purple"]),
  cards: z.array(flipCardSchema).length(4),
  className: z.string().optional(),
});

export const splitHeroSchema = z.object({
  type: z.literal("splitHero"),
  theme: z.enum(["blue", "purple", "blue-light"]),
  label: z.string().nullable(),
  title: z.string(),
  headingTag: z.enum(["h1", "h2", "h3"]).nullable(),
  subtitle: z.string(),
  subtitlePosition: z.enum(["left", "right"]).nullable(),
  description: richTextSchema.nullable(),
  cta: ctaSchema.nullable(),
  desktopImage: imageSchema.nullable(),
  className: z.string().optional(),
});

export const splitHeroImageSchema = z.object({
  type: z.literal("splitHeroImage"),
  theme: z.enum(["blue", "blue-gradient"]),
  label: z.string().nullable(),
  title: richTextSchema,
  headingTag: z.enum(["h1", "h2", "h3"]).nullable(),
  description: richTextSchema.nullable(),
  cta: ctaSchema.nullable(),
  desktopImage: imageSchema,
  className: z.string().optional(),
});

export const splitHeroVideoSchema = z.object({
  type: z.literal("splitHeroVideo"),
  title: richTextSchema,
  headingTag: z.enum(["h1", "h2", "h3"]).nullable(),
  description: richTextSchema.nullable(),
  cta: ctaSchema.nullable(),
  desktopVideoAnimation: videoSchema,
  mobileVideoAnimation: videoSchema.nullable(),
  className: z.string().optional(),
});

export const spotCardSchema = z.object({
  image: imageSchema,
  title: z.string(),
  link: z.string(),
});

export const spotTestimonialSchema = z.object({
  text: z.string(),
  image: imageSchema.nullable(),
  caption: z.string(),
  author: z.string(),
  title: z.string().optional(),
  company: z.string().optional(),
});

const spotSchema = z.object({
  type: z.literal("spot"),
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
  card: spotCardSchema,
  testimonial: spotTestimonialSchema,
});

export const interactiveCardsSchema = z.object({
  type: z.literal("interactiveCards"),
  image: imageSchema,
  cardsHotSpots: z.array(spotSchema),
  className: z.string().optional(),
});

export const disclaimerTextSchema = z.object({
  type: z.literal("disclaimerText"),
  content: richTextSchema,
  className: z.string().optional(),
});
